import type { ReactNode } from "react";

/**
 * Moldura das telas que não são o atendimento.
 *
 * O cabeçalho é discreto de propósito: estas telas são consultadas entre
 * eventos, sentado, e o que importa é o conteúdo — não a navegação.
 */
export function Pagina({
  titulo,
  descricao,
  acao,
  children,
}: {
  titulo: string;
  descricao?: string;
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-[1.75rem] leading-tight tracking-[-0.02em]">{titulo}</h1>
          {descricao && (
            <p className="mt-1 max-w-[52ch] text-[0.9375rem] leading-relaxed text-texto-suave">
              {descricao}
            </p>
          )}
        </div>
        {/* `max-w-full`: no celular a ação desce para a linha de baixo e quebra dentro dela, em vez de empurrar a
            página para os lados. */}
        {acao && <div className="min-w-0 max-w-full shrink-0">{acao}</div>}
      </header>
      {children}
    </div>
  );
}

/**
 * Lista responsiva.
 *
 * Cartões no celular, tabela em tela larga — a mesma marcação. Tabela de seis
 * colunas num aparelho de 390px vira rolagem horizontal, que ninguém usa.
 */
export function Lista<T>({
  itens,
  chave,
  cartao,
  colunas,
  linha,
  rodape,
}: {
  itens: T[];
  chave: (item: T) => string | number;
  /** Como o item aparece no celular. */
  cartao: (item: T) => ReactNode;
  /** Cabeçalhos da tabela, em tela larga. */
  colunas: string[];
  /** As células do item, em tela larga. */
  linha: (item: T) => ReactNode[];
  /**
   * Linha de fechamento — totais.
   *
   * Some quando não há itens: total de lista vazia é ruído. Vem separado de
   * `linha` porque não é mais um item; é a conclusão do que está acima, e se
   * distingue por peso e por borda, não por conteúdo.
   */
  rodape?: { celulas: ReactNode[]; cartao: ReactNode };
}) {
  const fecha = rodape && itens.length > 0;

  return (
    <>
      <ul className="flex flex-col gap-2 md:hidden">
        {itens.map((item) => (
          <li
            key={chave(item)}
            className="rounded-peca border border-borda bg-superficie px-4 py-3"
          >
            {cartao(item)}
          </li>
        ))}
        {fecha && (
          <li className="rounded-peca border border-borda-forte bg-superficie-alta px-4 py-3">
            {rodape.cartao}
          </li>
        )}
      </ul>

      <div className="hidden overflow-hidden rounded-peca border border-borda md:block">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-borda bg-superficie">
              {colunas.map((c) => (
                <th
                  key={c}
                  className="px-4 py-2.5 text-[0.75rem] font-semibold uppercase tracking-[0.1em] text-texto-fraco"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <tr key={chave(item)} className="border-b border-borda/60 last:border-0">
                {linha(item).map((celula, i) => (
                  <td key={i} className="px-4 py-3 align-middle text-[0.9375rem]">
                    {celula}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {fecha && (
            <tfoot>
              <tr className="border-t border-borda-forte bg-superficie">
                {rodape.celulas.map((celula, i) => (
                  <td
                    key={i}
                    className="px-4 py-3 align-middle text-[0.9375rem] font-medium text-texto"
                  >
                    {celula}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}
